import { signInWithAppleToken } from './googleSession'

/**
 * Sign in with Apple through the SHELL's native sheet — the iOS half of
 * guideline 4.8. In a browser the gate's Apple button is a plain anchor to
 * /auth/apple/start (Apple's web flow); inside the Capacitor shell on iOS the
 * platform's own sheet (ASAuthorizationController, via
 * @capacitor-community/apple-sign-in) is the credential surface, exactly as
 * StoreKit is the till: it hands the app an identityToken that the BFF verifies
 * in full against the bundle-id audience (/auth/apple/native).
 *
 * Feature-detected, never assumed: a shell built before the plugin was added
 * has no `SignInWithApple` and falls back to the web flow (whose hosts are in
 * capacitor.config.ts's allowNavigation for exactly that case). The plugin is
 * imported DYNAMICALLY inside the branch so a browser never downloads bridge
 * code.
 */

type CapacitorGlobal = {
  getPlatform?: () => string
  isPluginAvailable?: (name: string) => boolean
}

function capacitor(): CapacitorGlobal | null {
  const c = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor
  return c && typeof c === 'object' ? c : null
}

/**
 * Which app the shell IS, from `AvengentsId/<bundle id>` in the User-Agent
 * (capacitor.config.ts bakes it at `cap sync`). Null in a browser, and null in a
 * shell built before 2026-09-16 — absence, not a guess, exactly as the build stamp
 * behaves. Bounded so a hostile UA cannot hand this an arbitrary string.
 */
const UA_BUNDLE_ID = /\bAvengentsId\/([A-Za-z0-9][\w.-]{0,127})/

function shellBundleId(): string | null {
  try {
    return UA_BUNDLE_ID.exec(navigator.userAgent)?.[1] ?? null
  } catch {
    return null
  }
}

/**
 * iOS shell with the plugin compiled in — AND the store's own bundle id.
 *
 * THE DEV APP TAKES THE WEB FLOW, and that is not a limitation to route around.
 * Since 2026-09-16 the dev build is a second app with its own id
 * (`net.avengents.app.dev`, so the two can sit on one phone). Apple mints an
 * identity token whose audience is whichever app asked, and the BFF's native door
 * verifies exactly one audience — this one. A dev app calling the sheet would get
 * a perfectly valid token that our own server must refuse: a sign-in that fails
 * after the face scan, which is the worst place to fail.
 *
 * So a shell that is not the store app reports the sheet unavailable, and
 * `SignInGate` renders the plain anchor to /auth/apple/start instead. That path
 * works there: app-dev's BFF redirects to Apple with `client_id`
 * ai.avengents.app.web and its own callback, and Apple serves the sign-in page
 * rather than an invalid_request [measured 2026-09-16].
 *
 * If the BFF ever accepts a second audience, this is the line that changes, and it
 * should change only together with it.
 */
export function nativeAppleAvailable(): boolean {
  try {
    const c = capacitor()
    if (!c || c.getPlatform?.() !== 'ios' || c.isPluginAvailable?.('SignInWithApple') !== true) return false
    const id = shellBundleId()
    return id === null || id === NATIVE_CLIENT_ID
  } catch {
    return false
  }
}

export type NativeAppleOutcome = 'ok' | 'cancelled' | 'failed'

/**
 * The STORE app's bundle id — the audience Apple stamps on a token minted by that
 * app's own sheet, and the only one the BFF's native door verifies. The dev app
 * has a different id and therefore never reaches here; see `nativeAppleAvailable`.
 */
const NATIVE_CLIENT_ID = 'net.avengents.app'

export async function signInWithAppleNative(): Promise<NativeAppleOutcome> {
  const { SignInWithApple } = await import('@capacitor-community/apple-sign-in')
  let response: { identityToken?: string; authorizationCode?: string; givenName?: string | null; familyName?: string | null }
  try {
    const r = await SignInWithApple.authorize({
      clientId: NATIVE_CLIENT_ID,
      // Required by the plugin's shape; unused by the native sheet on iOS.
      redirectURI: `${window.location.origin}/auth/apple/callback`,
      scopes: 'email name',
      state: crypto.randomUUID(),
    })
    response = r.response
  } catch (e) {
    // ASAuthorizationError.canceled is 1001; the plugin surfaces it as a message.
    const msg = String((e as { message?: unknown })?.message ?? e)
    return /1001|cancel/i.test(msg) ? 'cancelled' : 'failed'
  }
  if (!response.identityToken) return 'failed'
  const name = [response.givenName, response.familyName].filter((p): p is string => !!p).join(' ')
  const ok = await signInWithAppleToken({
    idToken: response.identityToken,
    ...(response.authorizationCode ? { authorizationCode: response.authorizationCode } : {}),
    ...(name ? { name } : {}),
  })
  return ok ? 'ok' : 'failed'
}
